// src/client/renderer/character/eye-tuning.ts
//
// Per-head eye decal placement tuned via assets/character-tune.html.
// Missing heads intentionally fall back to bbox-derived defaults in the tuner.

import type { HeadId } from './types';

export interface EyePlacement {
  /** Head-local eye anchor [x, y, z]. */
  anchor: [number, number, number];
  /** Uniform scale applied to the eye preview geometry. */
  scale: number;
  /** Head-local Euler rotation [x, y, z] in radians. */
  rotation: [number, number, number];
}

export interface EyeTuning {
  /** Left eye placement for paired eye styles. */
  left?: EyePlacement;
  /** Right eye placement for paired eye styles. */
  right?: EyePlacement;
  /** Center eye placement for single-eye styles such as cyclops. */
  center?: EyePlacement;
}

export const EYE_TUNING: Partial<Record<HeadId, EyeTuning>> = {
  horse: {
    left: {
      anchor: [-0.2473, 0.0332, 0.3073],
      scale: 0.615,
      rotation: [0.0156, -0.0248, -0.0225],
    },
    right: {
      anchor: [-0.0912, 0.0374, 0.2621],
      scale: 0.615,
      rotation: [0.0, 0.0, 0.0],
    },
    center: {
      anchor: [-0.174, 0.0614, 0.3193],
      scale: 0.385,
      rotation: [0.0, 0.0, 0.0],
    },
  },
  chicken: {
    left: {
      anchor: [-0.2262, 0.0004, 0.5706],
      scale: 0.5,
      rotation: [0.0, 0.0, 0.0],
    },
    right: {
      anchor: [-0.04, 0.0044, 0.5297],
      scale: 0.5,
      rotation: [0.0, 0.0, 0.0],
    },
    center: {
      anchor: [-0.1294, 0.0511, 0.3545],
      scale: 0.4241,
      rotation: [0.0, 0.0, 0.0],
    },
  },
  'cardboard-box': {
    left: {
      anchor: [-0.0895, 0.1212, 0.3299],
      scale: 0.665,
      rotation: [0.0, 0.0, 0.0],
    },
    right: {
      anchor: [0.0818, 0.1217, 0.3299],
      scale: 0.665,
      rotation: [0.0, 0.0, 0.0],
    },
    center: {
      anchor: [-0.0072, 0.1936, 0.3299],
      scale: 0.645,
      rotation: [0.0, 0.0, 0.0],
    },
  },
  shark: {
    left: {
      anchor: [-0.321, 0.0839, 0.3255],
      scale: 0.6146,
      rotation: [0.0, 0.0, 0.0],
    },
    right: {
      anchor: [-0.021, 0.0713, 0.3065],
      scale: 0.6146,
      rotation: [0.0, 0.0, 0.0],
    },
    center: {
      anchor: [-0.2383, 0.0571, 0.2017],
      scale: 0.455,
      rotation: [0.0, 0.0, 0.0],
    },
  },
  pumpkin: {
    left: {
      anchor: [-0.1368, 0.0201, 0.4145],
      scale: 0.5,
      rotation: [0.0, 0.0, 0.0],
    },
    right: {
      anchor: [0.0561, 0.0196, 0.4049],
      scale: 0.5,
      rotation: [0.0, 0.0, 0.0],
    },
    center: {
      anchor: [-0.0423, 0.0594, 0.4095],
      scale: 0.575,
      rotation: [0.0, 0.0, 0.0],
    },
  },
  alien: {
    left: {
      anchor: [-0.2854, 0.0273, 0.3066],
      scale: 0.71,
      rotation: [0.0, 0.0, 0.0],
    },
    right: {
      anchor: [-0.0674, 0.0099, 0.2636],
      scale: 0.71,
      rotation: [0.0, 0.0, 0.0],
    },
    center: {
      anchor: [-0.1747, 0.1397, 0.286],
      scale: 0.71,
      rotation: [0.0, 0.0, 0.0],
    },
  },
  unicorn: {
    left: {
      anchor: [-0.258, 0.0079, 0.3499],
      scale: 0.5,
      rotation: [0.0, 0.0, 0.0],
    },
    right: {
      anchor: [-0.0923, 0.0046, 0.2956],
      scale: 0.5,
      rotation: [0.0, 0.0, 0.0],
    },
    center: {
      anchor: [-0.1841, 0.073, 0.2844],
      scale: 0.405,
      rotation: [0.0, 0.0, 0.0],
    },
  },
  'traffic-cone': {
    left: {
      anchor: [-0.0684, -0.0321, 0.3282],
      scale: 0.665,
      rotation: [0.0, 0.0, 0.0],
    },
    right: {
      anchor: [0.1082, -0.033, 0.3382],
      scale: 0.665,
      rotation: [0.0, 0.0, 0.0],
    },
    center: {
      anchor: [0.021, 0.0574, 0.3382],
      scale: 0.66,
      rotation: [0.0, 0.0, 0.0],
    },
  },
};
